import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";
import { compose } from "redux";

import { useMount } from "app/custom-hooks/useMount";
import { translate } from "app/filters/i18nFilter";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import { MANAGE_INTEGRATION_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import withSecondaryBarSet from "app/pages/sales-intelligence/hoc/withSecondaryBarSet";
import usePreventLeavePage from "app/pages/sales-intelligence/hooks/usePreventLeavePage";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import { fetchContactsStatusThunk } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/effects";
import withResetManagementExport from "app/pages/sales-intelligence/sub-modules/management/hocs/manage-export/withResetManagementExport";
import withMappingFieldsCall from "app/pages/sales-intelligence/sub-modules/management/hocs/withMappingFieldsCall";
import { ExportNotification } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";

import withAccountExclusionRequests from "../manage-integration/hocs/withAccountExclusionRequests";
import { withConnectionCheck } from "../manage-integration/hocs/withConnectionCheck";
import withIntegrationAccess from "../manage-integration/hocs/withIntegrationAccess";
import { withMappingsAndColumnsFetch } from "../manage-integration/hocs/withMappingsAndColumnsFetch";
import { StyledTabContent } from "../manage-integration/styles/styles";
import {
    MANAGEMENT_MANAGE_EXPORT_DYNAMICS_SUB_TITLE,
    MANAGEMENT_MANAGE_EXPORT_HUBSPOT_SUB_TITLE,
    MANAGEMENT_MANAGE_EXPORT_SALESFORCE_SUB_TITLE,
    MANAGEMENT_MANAGE_EXPORT_TITLE,
    MISSING_FIELDS_MESSAGE,
} from "../management/translations/integrationsKeys";

import { AddFieldsModal } from "./components/AddFieldsModal/AddFieldsModal";
import { DiscardModal } from "./components/DiscardModal/DiscardModal";
import { ExportFooter } from "./components/ExportFooter/ExportFooter";
import { ExportTabHeader } from "./components/ExportTabHeader/ExportTabHeader";
import { ManageExportBreadcrumbs } from "./components/ManageExportBreadcrumbs/ManageExportBreadcrumbs";
import { withCacheToken } from "./hoc/withCacheToken";
import { useManageExport } from "./hooks/useManageExport";
import { useMissingFieldsWarning } from "./hooks/useMissingFieldsWarning";
import {
    StyledExportPage,
    StyledExportPageContent,
    StyledExportPageTitle,
    StyledExportPageSubTitle,
    StyledMissingRequiredFieldsWarning,
} from "./style";
import { getExportTabsDisplayNames } from "./utils/utils";


const ManageExport = () => {
    const dispatch = useDispatch();

    const [isOpenAddFieldsModal, setIsOpenAddFieldsModal] = useState(false);

    const { integrationName } = getIntegrationNameFromUrl() as { integrationName: Integration };

    const isLeadsVisible = integrationName !== ExportIntegrations.hubspot;
    const exportTabsDisplayNames = getExportTabsDisplayNames(integrationName, translate);

    const {
        onClickSave,
        onClickCancel,
        setNotification,
        setTabIndex,
        cleanup,
        isSettingsLoading,
        isSaveSettingsLoading,
        isDisabledCancel,
        isDisabledSave,
        isFooterVisible,
        isSettingsChanged,
        tabIndex,
        tabName,
        missingRequiredFieldsCount,
        saveTooltipContent,
        tabsWithErrors,
    } = useManageExport(integrationName);

    const missingFieldsCounts = useMissingFieldsWarning({ missingRequiredFieldsCount, integrationName });

    useEffect(() => {
        return () => {
            setNotification(null);
        };
    }, [tabIndex]);

    const { setConfirm: confirmLeavePage, resetRedirectToState } = usePreventLeavePage(
        isSettingsChanged,
        () => setNotification(ExportNotification.UNSAVED_FIELDS),
        [],
        { name: MANAGE_INTEGRATION_ROUTE[integrationName], params: {} },
    );

    useMount(() => {
        void dispatch(fetchContactsStatusThunk());
        return cleanup;
    });

    const handleSave = async () => {
        await onClickSave();
        resetRedirectToState();
        confirmLeavePage(true);
        integrationTrackingService.trackSaveChanges(integrationName);
    };
    const handleCancel = () => {
        resetRedirectToState();
        onClickCancel();
    };

    const handleClickAddFields = () => setIsOpenAddFieldsModal(true);
    const handleClose = () => setIsOpenAddFieldsModal(false);

    const handleDiscardModalConfirm = () => confirmLeavePage(true);

    const onSelectedTabChange = (index: number) => {
        integrationTrackingService.trackSelectMappingTab(exportTabsDisplayNames[index], integrationName);
        setTabIndex(index);
    };

    return (
        <StyledExportPage>
            <ManageExportBreadcrumbs integrationName={integrationName} />
            <StyledExportPageContent>
                <StyledExportPageTitle>
                    {translate(MANAGEMENT_MANAGE_EXPORT_TITLE, {
                        integrationName: getCrmName(integrationName),
                    })}
                </StyledExportPageTitle>
                <StyledExportPageSubTitle>
                    {translate(
                        integrationName === "hubspot"
                            ? MANAGEMENT_MANAGE_EXPORT_HUBSPOT_SUB_TITLE
                            : integrationName === "dynamics"
                              ? MANAGEMENT_MANAGE_EXPORT_DYNAMICS_SUB_TITLE
                              : MANAGEMENT_MANAGE_EXPORT_SALESFORCE_SUB_TITLE,
                    )}
                </StyledExportPageSubTitle>
                {missingFieldsCounts.length === 0 ? null : (
                    <StyledMissingRequiredFieldsWarning>{`${translate(
                        MISSING_FIELDS_MESSAGE,
                    )} ${missingFieldsCounts.join(", ")}`}</StyledMissingRequiredFieldsWarning>
                )}
                <StyledTabContent>
                    <ExportTabHeader
                        tabsWithErrors={tabsWithErrors}
                        isLoading={isSettingsLoading}
                        onAddFields={handleClickAddFields}
                        onSelect={onSelectedTabChange}
                        selectedIndex={tabIndex}
                        isLeadsVisible={isLeadsVisible}
                        tabsDisplayName={exportTabsDisplayNames}
                    />
                </StyledTabContent>
                <AddFieldsModal
                    isOpen={isOpenAddFieldsModal}
                    onClose={handleClose}
                    exportName={tabName}
                />
            </StyledExportPageContent>
            <ExportFooter
                onSave={handleSave}
                onCancel={handleCancel}
                isDisabledCancel={isDisabledCancel}
                isDisabledSave={isDisabledSave}
                isLoading={isSaveSettingsLoading}
                isVisible={isFooterVisible}
                saveTooltipContent={saveTooltipContent}
            />
            <DiscardModal
                tab={exportTabsDisplayNames[tabIndex]}
                setConfirm={handleDiscardModalConfirm}
            />
        </StyledExportPage>
    );
};

const ManageExportPageContainer = compose(
    withConnectionCheck,
    withAccountExclusionRequests,
    withIntegrationAccess,
    withMappingFieldsCall,
    withResetManagementExport,
    withMappingsAndColumnsFetch({ refetchIfExists: false }),
    withSecondaryBarSet("SalesIntelligenceManagement"),
    withCacheToken,
)(ManageExport);

export default ManageExportPageContainer;
